// So far all of our modals follow the same system for modal-izing, using float-ui.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
  useTransitionStatus,
} from '@floating-ui/react';
import { cva } from 'class-variance-authority';
import { useHotkeys } from 'react-hotkeys-hook';

import { cn } from '../../lib/utils';

import { useKeyMaster } from '../../contexts/KeyMaster';

// A context so we can easily access the unlocked function from child components, e.g, the modals themselves
const ModalContext = createContext({ unlocked: () => true, requestClose: () => {} });
export const useModalContext = () => useContext(ModalContext);

export const useRetainedModalValue = (value) => {
  const [retainedValue, setRetainedValue] = useState(value);

  useEffect(() => {
    if (value != null) setRetainedValue(value);
  }, [value]);

  return value ?? retainedValue;
};

const MODAL_TRANSITION_DURATION = 200;

const portalSizeVariants = cva('w-full', {
  variants: {
    size: {
      default: '',
      document: 'h-[90dvh] max-w-5xl',
      form: 'max-w-md',
      mini: 'max-h-150 max-w-sm',
      settings: 'h-full max-h-150 max-w-xl',
      browser: 'h-[80dvh] max-h-150 max-w-xl',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export const ModalPortal = ({
  open,
  onOpenChange,
  onClose,
  children,
  className = '',
  overlayClassName = '',
  backdropClassName = '',
  size,
  keychainId = null,
  outsidePressEvent = 'mousedown',
}) => {
  const isOpen = Boolean(open);
  const { register, unregister, unlocked: checkUnlocked } = useKeyMaster();
  const unlocked = useCallback(() => {
    if (!keychainId) return true;
    return checkUnlocked(keychainId);
  }, [checkUnlocked, keychainId]);

  // A single close path. Hosts may pass `onClose` (a stable callback fired on any dismissal) and/or
  // `onOpenChange(nextOpen, event)` for the full FloatingUI signal. `onClose` is the preferred, lint-friendly
  // form: it lets call sites pass a stable useCallback instead of a bare inline `(nextOpen) => { if (!nextOpen) ... }`.
  const closeModal = useCallback(
    function closeModal(event = null) {
      onOpenChange?.(false, event);
      onClose?.(event);
    },
    [onClose, onOpenChange],
  );

  const requestClose = useCallback(
    function requestModalClose(event = null) {
      if (!unlocked()) return;
      closeModal(event);
    },
    [closeModal, unlocked],
  );

  const handleOpenChange = useCallback(
    function handleFloatingOpenChange(nextOpen, event) {
      if (nextOpen) {
        onOpenChange?.(true, event);
        return;
      }

      requestClose(event);
    },
    [onOpenChange, requestClose],
  );

  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
  });
  const dismiss = useDismiss(context, { escapeKey: false, outsidePressEvent });
  const { getFloatingProps } = useInteractions([dismiss]);
  const { isMounted, status } = useTransitionStatus(context, { duration: MODAL_TRANSITION_DURATION });

  useEffect(() => {
    if (!keychainId || !isMounted) return undefined;

    register(keychainId);
    return () => {
      unregister(keychainId);
    };
  }, [isMounted, keychainId, register, unregister]);

  // Close any open context menus when a modal opens
  useEffect(() => {
    if (isOpen) window.dispatchEvent(new CustomEvent('output:close-context-menu'));
  }, [isOpen]);

  // Handle escape at this level, so we don't have to repeat the code in each modal
  useHotkeys(
    'escape',
    () => {
      requestClose();
    },
    {
      enabled: isOpen && !!keychainId && (!!onOpenChange || !!onClose),
      enableOnFormTags: true,
      enableOnContentEditable: true,
      preventDefault: true,
    },
  );

  if (!isMounted) return null;

  return (
    <FloatingPortal>
      <FloatingOverlay className={cn('relative z-(--stack-modal)', overlayClassName)} lockScroll>
        <div
          className={cn(
            'fixed inset-0 bg-black/70 opacity-0 transition-opacity duration-200 ease-out data-[status=close]:ease-in data-[status=open]:opacity-100 motion-reduce:transition-none',
            backdropClassName,
          )}
          data-status={status}
        />
        <div className="flex h-full items-center justify-center p-4 max-md:px-2">
          <FloatingFocusManager context={context}>
            <div
              ref={refs.setFloating}
              data-status={status}
              {...getFloatingProps()}
              className={cn(portalSizeVariants({ size }), 'modal-surface-fade', className)}
            >
              <ModalContext.Provider value={{ unlocked, requestClose }}>{children}</ModalContext.Provider>
            </div>
          </FloatingFocusManager>
        </div>
      </FloatingOverlay>
    </FloatingPortal>
  );
};
